// src/pages/Connector/GoogleDrive/PendingUpdateModalForDrive.js
import React, { useEffect, useState } from "react";
import { Modal as MuiModal, Box, Button as MuiButton, Typography, Divider, Stack, Alert, CircularProgress } from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TiptapEditor from "../../../../components/Common/TiptapEditor";
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { marked } from 'marked';

export default function PendingUpdateModalForDrive({ 
    isOpen, 
    onClose, 
    onAccept, 
    onReject, 
    originalContentHTML, 
    pendingContentHTML,  
    documentUrl, 
    isLoadingAccept,
    isLoadingReject, 
    permission
}) {
    const modalStyle = {
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '85vw' }, maxWidth: '1400px', maxHeight: '90vh',
        bgcolor: 'background.paper', boxShadow: 24, p: 3, borderRadius: '8px', outline: 'none',
        display: 'flex', flexDirection: 'column'
    };

    const commonExtensions = [
        StarterKit.configure({heading: {levels: [1,2,3,4,5,6]}}), 
        Link.configure({openOnClick: false, autolink: true, linkOnPaste: true, HTMLAttributes: {target: '_blank'}}), 
        Underline, 
        Table.configure({resizable: true}), 
        TableCell, TableHeader, TableRow
    ];

    // Convert markdown to HTML for proper rendering
    const originalContentAsHtml = originalContentHTML ? marked.parse(originalContentHTML) : "<p></p>";
    const pendingContentAsHtml = pendingContentHTML ? marked.parse(pendingContentHTML) : "<p></p>";

    const originalEditor = useEditor({
        extensions: commonExtensions,
        content: originalContentAsHtml,
        editable: false,
        editorProps: { attributes: { class: 'tiptap-readonly-viewer' } },
    });

    const pendingEditor = useEditor({
        extensions: commonExtensions,
        content: pendingContentAsHtml,
        editable: permission.canModify,
    });
    
    useEffect(() => {
        if (originalEditor) {
            const newContent = originalContentHTML ? marked.parse(originalContentHTML) : "<p></p>";
            const currentContent = originalEditor.getHTML();
            if (currentContent !== newContent) {
                originalEditor.commands.setContent(newContent);
            }
        }
        if (pendingEditor) {
            const newContent = pendingContentHTML ? marked.parse(pendingContentHTML) : "<p></p>";
            const currentContent = pendingEditor.getHTML();
            if (currentContent !== newContent) {
                pendingEditor.commands.setContent(newContent);
            }
        }
    }, [originalContentHTML, pendingContentHTML, originalEditor, pendingEditor]);

    const handleAcceptClick = () => {
        if (pendingEditor) {
            onAccept(pendingEditor.getHTML());
        }
    };

    return (
        <MuiModal open={isOpen} onClose={onClose} aria-labelledby="gdrive-pending-update-title">
            <Box sx={modalStyle}>
                <Typography variant="h6" id="gdrive-pending-update-title">Review Pending Update</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>{documentUrl}</Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                    The content from Google Drive has been updated. Review and edit the new version below. Choose to accept your edited version (overwriting your previous manual edits) or reject it (keeping your current version).
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexGrow: 1, overflow: 'hidden' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #ddd', borderRadius: '4px' }}>
                        <Typography sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd' }} fontWeight={500}>Your Current Version</Typography>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1, '& .ProseMirror': { p: 1.5, outline: 'none' } }}>
                            {originalEditor ? <EditorContent editor={originalEditor}/> : <CircularProgress />}
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #a0d3a0', borderRadius: '4px' }}>
                         <Typography sx={{ p: 1.5, bgcolor: '#e6ffed', borderBottom: '1px solid #a0d3a0' }} fontWeight={500}>New Version from Google Drive (Editable)</Typography>
                         <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                            {pendingEditor ? <TiptapEditor editor={pendingEditor} height="100%" /> : <CircularProgress />}
                         </Box>
                    </Box>
                </Box>
                    
                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 2, flexShrink: 0 }}>
                    <MuiButton variant="outlined" color="secondary" onClick={onClose}>Close</MuiButton>
                    {permission.canModify && (
                        <>
                            <MuiButton variant="outlined" color="error" onClick={onReject} disabled={isLoadingReject || isLoadingAccept} startIcon={isLoadingReject ? <CircularProgress size={20}/> : <CloseIcon/>}>
                                Reject New Version
                            </MuiButton>
                            <MuiButton variant="contained" color="primary" onClick={handleAcceptClick} disabled={isLoadingAccept || isLoadingReject || !pendingEditor} startIcon={isLoadingAccept ? <CircularProgress size={20}/> : <SaveIcon/>}>
                                Accept & Save Version
                            </MuiButton>
                        </>
                    )}
                </Stack>
            </Box>
        </MuiModal>
    );
}